// fetchFresh — one RTK Query request that is safe to run beside others.
//
// `useLazy…Query` triggers share a single subscription, so calling one over
// many arguments at once loses every result but the last (see settleInTurn).
// `dispatch(endpoint.initiate(arg))` gives every call a subscription of its
// own. `unwrap()` reads the result back out of the cache, and an endpoint with
// `keepUnusedDataFor: 0` drops an entry the moment nothing subscribes to it, so
// the subscription is kept until the result has been read and only then
// released. (`{ subscribe: false }` does not work here: it leaves nothing to
// keep the entry alive and `unwrap()` resolves `undefined`.) `forceRefetch`
// makes the call always go to the server, as the lazy trigger's
// `refetchOnMountOrArgChange` did.

type InitiateOptions = { forceRefetch: true };

/** The slice of an RTK Query endpoint this helper relies on. */
export type Initiate<Arg> = (arg: Arg, options: InitiateOptions) => unknown;

type Request<R> = { unwrap: () => Promise<R>; unsubscribe: () => void };

/** Run `initiate(arg)` through `dispatch` and resolve to the response data. */
export default async function fetchFresh<Arg, R = any>(
  dispatch: (action: never) => unknown,
  initiate: Initiate<Arg>,
  arg: Arg,
): Promise<R> {
  const request = dispatch(initiate(arg, { forceRefetch: true }) as never) as Request<R>;
  try {
    return await request.unwrap();
  } finally {
    request.unsubscribe();
  }
}
